import { useEffect, useState } from "react";
import { Bot, Sparkles, X, Plus } from "lucide-react";
import api, { API, readCsrfToken } from "../../lib/api";
import { consumeSSE } from "../../lib/sse";
import { useSentinelBusy } from "../../lib/sentinel-busy";

export default function SuggestPanel({ trainee, onClose, onAssigned }) {
  const [reasoning, setReasoning] = useState("");
  const [streaming, setStreaming] = useState(true);
  const [course, setCourse] = useState(null);
  const [err, setErr] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const { startBusy } = useSentinelBusy();

  useEffect(() => {
    let cancelled = false;
    const stopBusy = startBusy();
    (async () => {
      try {
        const res = await fetch(`${API}/squad/${trainee.id}/suggest/stream`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(readCsrfToken() ? { "X-CSRF-Token": readCsrfToken() } : {}),
          },
        });
        await consumeSSE(res, ({ event, data }) => {
          if (cancelled) return false;
          if (event === "delta") {
            setReasoning((r) => r + data);
          } else if (event === "final") {
            try {
              const parsed = JSON.parse(data);
              setCourse(parsed.course);
              if (parsed.reasoning) setReasoning(parsed.reasoning);
            } catch {
              /* ignore parse failure; rely on streamed reasoning */
            }
          } else if (event === "error") {
            setErr(data || "Sentinel stream error");
            setStreaming(false);
            return false;
          } else if (event === "done") {
            setStreaming(false);
            return false;
          }
          return true;
        });
        if (!cancelled) setStreaming(false);
      } catch (e) {
        if (!cancelled) {
          setErr(e?.message || "Sentinel unavailable");
          setStreaming(false);
        }
      } finally {
        stopBusy();
      }
    })();
    return () => { cancelled = true; stopBusy(); };
  }, [trainee.id, startBusy]);

  const assignNow = async () => {
    if (!course) return;
    setAssigning(true);
    try {
      await api.post(`/squad/${trainee.id}/assign`, { course_id: course.id });
      onAssigned?.(course);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="suggest-modal"
    >
      <div
        className="w-full max-w-lg flat-card p-6 scanlines relative max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[var(--text-2)] hover:text-white"
          data-testid="suggest-close"
          aria-label="Close suggestion"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-3 mb-1">
          <Bot size={22} color="var(--cyan)" strokeWidth={1.5} />
          <div className="overline">// SENTINEL_RECOMMENDATION</div>
        </div>
        <h2 className="font-display font-bold text-2xl uppercase">Next Mission for {trainee.name}</h2>
        <div className="font-mono text-xs text-[var(--text-2)] mt-1">TRK / {trainee.track}</div>

        {err && (
          <div className="mt-6 border border-[var(--red)] p-3 font-mono text-xs text-[var(--red)]" data-testid="suggest-error">
            {err}
          </div>
        )}

        <div className="mt-5 flex-1 overflow-y-auto pr-1" data-testid="suggest-content">
          <div className="overline mb-2">// SENTINEL_REASONING</div>
          <pre
            className="whitespace-pre-wrap text-[var(--text)] text-sm leading-relaxed font-mono"
            data-testid="suggest-reasoning"
          >
            {reasoning}
            {streaming && <span className="text-[var(--green)] blink">▌</span>}
          </pre>

          {course && (
            <div className="mt-5 border border-[var(--cyan)] p-4" data-testid="suggest-course">
              <div className="flex items-center gap-2">
                <Sparkles size={14} color="var(--cyan)" />
                <span className="font-mono text-xs text-[var(--cyan)] tracking-widest uppercase">Recommended Course</span>
              </div>
              <div className="font-mono text-xs text-[var(--cyan)] mt-3" data-testid="suggest-code">{course.code}</div>
              <div className="font-display font-bold text-xl uppercase">{course.title}</div>
              <div className="font-mono text-[10px] text-[var(--text-2)] tracking-widest uppercase mt-1">
                {course.stage} &middot; {course.duration_hours}h
              </div>
              <p className="text-sm text-[var(--text-2)] mt-2">{course.description}</p>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-4 mt-4 border-t border-[var(--border)]">
          <button
            onClick={assignNow}
            disabled={!course || assigning || streaming}
            className="btn-primary inline-flex items-center gap-2 flex-1 justify-center"
            data-testid="suggest-assign"
          >
            <Plus size={14} /> {assigning ? "Assigning…" : streaming ? "Awaiting Sentinel…" : "Assign Now"}
          </button>
          <button onClick={onClose} className="btn-ghost" data-testid="suggest-dismiss">
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
